import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Stack, TextField, Button, Typography, Link} from 'views/components';
import {MarkunreadMailboxOutlined} from 'views/icons';
import {Translate} from 'views/module';
import {loginWithOtp} from '../../../../utils/auth/otp';
import {startUserLogIn} from '../../../../utils/auth/login';
import {connect, dispatch} from '../../../../utils/state_manager';

const OtpBox = (props) => {
  const {
    email,
    setChallengeName,

    userReload,
    statusProgressWrap: progressWrap,
    statusClear,
  } = props;

  const [otp, setOtp] = React.useState(['', '', '', '', '', '']);
  const [loading, setLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState(false);
  const inputRefs = React.useRef([]);
  const timerRef = React.useRef(null);
  const [secondsLeft, setSecondsLeft] = React.useState(30);

  const isOtpValid = Boolean(otp.every((digit) => digit !== ''));
  const isOtpEmpty = Boolean(otp.every((digit) => digit === ''));
  const canResend = secondsLeft === 0 && !loading;
  const formattedCountdown = secondsLeft > 0 ? ` 00:${String(secondsLeft).padStart(2, '0')}` : '';

  const startTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setSecondsLeft(30);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  React.useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [email, startTimer]);

  const handleChange = (value, index) => {
    // Only allow alphanumeric input, convert to uppercase
    if (!/^[a-zA-Z0-9]*$/.test(value || '')) return;

    const newOtp = [...otp];
    newOtp[index] = value?.toUpperCase();
    setOtp(newOtp);

    // Auto-focus to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace to move to previous input
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedChars = pastedData.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase();

    const newOtp = pastedChars.split('');
    while (newOtp.length < 6) {
      newOtp.push('');
    }
    setOtp(newOtp);

    // Focus last filled input or the 6th input
    const focusIndex = Math.min(pastedChars.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOtpValid) {
      return;
    }

    const otpValue = otp.join('');
    if (otpValue.length === 6) {
      progressWrap(async () => {
        setLoginError(false);
        setLoading(true);
        try {
          await loginWithOtp(otpValue, email);
          userReload();
          statusClear();
        } finally {
          setLoading(false);
        }
      }, {
        onErrorMessage: () => {
          setLoginError(true);
        },
      });
    }
  };

  const handleResendCode = async () => {
    startTimer();
    setOtp(['', '', '', '', '', '']);
    setLoginError(false);
    progressWrap(async () => {
      await startUserLogIn(email);
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack
        alignItems="center"
        spacing={{xs: 2, sm: 4}}
      >
        <MarkunreadMailboxOutlined
          sx={{
            height: {
              xs: 36, sm: 52,
            },
            width: {
              xs: 36, sm: 52,
            },
            color: 'text.contrast',
          }}
        />
        {/* Titles */}
        <Stack spacing={1} alignItems="center">
          <Typography variant="h4" fontWeight="normal" align="center" color="text.primary">
            <Translate expression="auth.OtpBox.Title" />
          </Typography>
          <Typography variant="body1" color="text.primary" align="center">
            <Translate expression="auth.OtpBox.Description" />
            {' '}
            <Box component="span" fontWeight="bold" display="inline">
              {email}
            </Box>
          </Typography>
        </Stack>
        {/* Password */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
        >
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              inputProps={{
                maxLength: 1,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  'height': {xs: 64, sm: 72, md: 80},
                  '& fieldset': {
                    borderColor: digit ? '#DA7955' : '#DED8D7',
                  },
                  '&:hover fieldset': {
                    borderColor: '#DA7955',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#DA7955',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: 'clamp(18px, 5vw, 24px)',
                  lineHeight: 1.15,
                  fontWeight: 'medium',
                  textAlign: 'center',
                  padding: 0,
                  height: '100%',
                  boxSizing: 'border-box',
                },
              }}
              disabled={loading}
            />
          ))}
        </Stack>
        {/* Submit and Send Again */}
        <Stack spacing={2} alignItems="center" width="100%">
          {
            isOtpEmpty &&
            <Button
              type="submit"
              variant={canResend ? 'contained' : 'outlined'}
              fullWidth
              disabled={!canResend}
              onClick={handleResendCode}
              sx={{
                'py': 1,
                'borderRadius': 2,
                'textTransform': 'none',
                'backgroundColor': canResend ? '#DA7955' : 'color.white',
                'color': canResend ? 'color.white' : '#2D2925B8',
                '&.Mui-disabled': {
                  color: '#2D2925B8',
                  backgroundColor: 'color.white',
                },
              }}
            >
              <Typography>
                <Translate expression="auth.OtpBox.ResendButton" />
                {!canResend && formattedCountdown}
              </Typography>
            </Button>
          }
          {
            !isOtpEmpty &&
              <Button
                type="submit"
                variant={isOtpValid ? 'contained' : 'outlined'}
                fullWidth
                disabled={!isOtpValid}
                loading={loading}
                sx={{
                  'py': 1,
                  'borderRadius': 2,
                  'textTransform': 'none',
                  'backgroundColor': isOtpValid ? '#DA7955' : 'color.white',
                  'color': isOtpValid ? 'color.white' : '#2D2925B8',
                }}
              >
                <Translate expression="auth.OtpBox.SubmitButton" />
              </Button>
          }
          {
            loginError &&
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                }}
              >
                <Translate expression='auth.NotAuthorizedException' />
              </Alert>
          }
        </Stack>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={0.5} alignItems="center">
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            <Translate expression="auth.OtpBox.ChangeEmailPrompt" />
          </Typography>
          <Link
            component="button"
            variant="body1"
            type="button"
            onClick={() => setChallengeName('START')}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Translate expression="auth.OtpBox.ChangeEmailLink" />
          </Link>
        </Stack>
      </Stack>
    </Box>
  );
};

OtpBox.propTypes = {
  email: PropTypes.string.isRequired,
  setChallengeName: PropTypes.func.isRequired,

  statusProgressWrap: PropTypes.func.isRequired,
  statusClear: PropTypes.func.isRequired,
  userReload: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, dispatch)(React.memo(OtpBox));
